import { useEffect, useRef, useState } from 'react'
import { useFormikContext } from 'formik'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
// import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
// import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
// import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import type { SxProps, Theme } from '@mui/material/styles'
import {
  TAG_COLORS,
  DEFAULT_TAG_COLOR,
  getTagChipSx,
  getTagDotColor,
  getTagPastel,
  getTagPastelColors,
} from './constants'
import { useTags } from './utils/useTags'
import useTranslate from '@/hooks/use-translate'
import { Tag } from '@/api'

// ── Color picker ──────────────────────────────────────────────────────────────

function ColorPicker({
  value,
  onChange,
}: {
  value: string
  onChange: (c: string) => void
}) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1, px: 1 }}>
      {TAG_COLORS.map((color) => {
        const isSelected = getTagPastel(value) === getTagPastel(color)
        return (
          <Box
            key={color}
            onClick={() => onChange(color)}
            sx={(theme) => {
              const pastel = getTagPastelColors(color, theme.palette.mode)
              return {
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: pastel.bg,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: isSelected ? pastel.fg : pastel.border,
                outline: isSelected ? `1px solid ${pastel.fg}` : 'none',
                transition: 'transform 0.1s',
                '&:hover': { transform: 'scale(1.2)' },
              }
            }}
          />
        )
      })}
    </Stack>
  )
}

// ── Inline tag edit form (dans le dropdown) ───────────────────────────────────

function TagEditForm({
  tag,
  onSubmit,
  onCancel,
  isLoading,
}: {
  tag: Tag
  onSubmit: (text: string, color: string) => void
  onCancel: () => void
  isLoading: boolean
}) {
  const [text, setText] = useState(tag.text ?? '')
  const [color, setColor] = useState(tag.color ?? DEFAULT_TAG_COLOR)

  return (
    <Box sx={{ px: 1, py: 0.75 }} onClick={(e) => e.stopPropagation()}>
      <TextField
        autoFocus
        variant="outlined"
        size="small"
        fullWidth
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Enter' && text.trim()) onSubmit(text.trim(), color)
          if (e.key === 'Escape') onCancel()
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box
                sx={(theme) => ({
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: getTagDotColor(color, theme.palette.mode),
                  flexShrink: 0,
                })}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Stack direction="row">
                <IconButton size="small" onClick={onCancel} sx={{ p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => text.trim() && onSubmit(text.trim(), color)}
                  disabled={!text.trim() || isLoading}
                  sx={{ p: 0.25 }}
                >
                  {isLoading ? (
                    <CircularProgress size={12} />
                  ) : (
                    <CheckIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Stack>
            </InputAdornment>
          ),
        }}
      />
      <ColorPicker value={color} onChange={setColor} />
    </Box>
  )
}

// ── Inline create form ────────────────────────────────────────────────────────

function TagCreateForm({
  onSubmit,
  onCancel,
  isLoading,
  initialText = '',
}: {
  onSubmit: (text: string, color: string) => void
  onCancel: () => void
  isLoading: boolean
  initialText?: string
}) {
  const [text, setText] = useState(initialText)
  const [color, setColor] = useState(DEFAULT_TAG_COLOR)
  const { t } = useTranslate('dashboard')

  return (
    <Box
      sx={{ px: 1, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
      onClick={(e) => e.stopPropagation()}
    >
      <TextField
        autoFocus
        variant="outlined"
        size="small"
        fullWidth
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('tagName')}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Enter' && text.trim()) onSubmit(text.trim(), color)
          if (e.key === 'Escape') onCancel()
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box
                sx={(theme) => ({
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: getTagDotColor(color, theme.palette.mode),
                  flexShrink: 0,
                })}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Stack direction="row">
                <IconButton size="small" onClick={onCancel} sx={{ p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => text.trim() && onSubmit(text.trim(), color)}
                  disabled={!text.trim() || isLoading}
                  sx={{ p: 0.25 }}
                >
                  {isLoading ? (
                    <CircularProgress size={12} />
                  ) : (
                    <CheckIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Stack>
            </InputAdornment>
          ),
        }}
      />
      <ColorPicker value={color} onChange={setColor} />
    </Box>
  )
}

// ── Option type ───────────────────────────────────────────────────────────────

type TagOption = Tag | { inputValue: string; text: string }

const filter = createFilterOptions<TagOption>()

// ── TagsField ─────────────────────────────────────────────────────────────────

interface TagsFieldProps {
  name?: string
  // groupId?: string
  /** Hides the built-in floating label, for callers that render their own label externally. */
  hideLabel?: boolean
  placeholder?: string
  inputSx?: SxProps<Theme>
}

export function TagsField({
  name = 'tagIds',
  hideLabel,
  placeholder,
  inputSx,
}: TagsFieldProps) {
  const { t } = useTranslate('dashboard')
  const { values, setFieldValue } = useFormikContext<Record<string, string[]>>()
  const {
    tags,
    isLoading,
    // isGeneric,
    // createTag,
    // patchTag,
    // deleteTag,
    // isCreating,
    // isPatching,
  } = useTags()

  const [editingTag, setEditingTag] = useState<Tag | null>(null)
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [createInitialText, setCreateInitialText] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const popperRef = useRef<HTMLDivElement | null>(null)

  // Fermer au clic à l'extérieur (nécessaire en mode contrôlé quand le focus est dans le listbox, ex. formulaire création)
  useEffect(() => {
    if (!open) return
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      const insideRoot = rootRef.current?.contains(target)
      const insidePopper = popperRef.current?.contains(target)
      if (!insideRoot && !insidePopper) {
        setOpen(false)
        setIsCreatingNew(false)
        setCreateInitialText('')
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [open])

  const selectedTagIds: string[] = values[name] ?? []
  const selectedTags = tags.filter((tag) =>
    selectedTagIds.includes(tag.id ?? '')
  )

  const handleChange = (_: unknown, newValue: TagOption[]) => {
    // Filtre les vrais tags (pas les options "créer")
    const ids = newValue
      .filter((v): v is Tag => 'id' in v && !!v.id)
      .map((v) => v.id as string)
    setFieldValue(name, ids)
  }

  // const handleUpdate = (text: string, color: string) => {
  //   if (!editingTag?.id) return
  //   patchTag(
  //     { id: editingTag.id, payload: { text, color } },
  //     { onSuccess: () => setEditingTag(null) }
  //   )
  // }

  // const handleDelete = (tag: Tag, e: React.MouseEvent) => {
  //   e.stopPropagation()
  //   if (!tag.id) return
  //   setDeletingId(tag.id)
  //   // Retirer aussi de la sélection
  //   setFieldValue(
  //     name,
  //     selectedTagIds.filter((id) => id !== tag.id)
  //   )
  //   deleteTag(tag.id, { onSettled: () => setDeletingId(null) })
  // }

  // const handleCreate = (text: string, color: string) => {
  //   createTag(
  //     { text, color },
  //     {
  //       onSuccess: (newTag) => {
  //         // Auto-sélectionner le tag créé
  //         if (newTag?.id) setFieldValue(name, [...selectedTagIds, newTag.id])
  //         setIsCreatingNew(false)
  //         setCreateInitialText('')
  //       },
  //     }
  //   )
  // }

  return (
    <Box ref={rootRef}>
      <Autocomplete
        multiple
        value={selectedTags}
        options={tags}
        loading={isLoading}
        getOptionLabel={(option) =>
          'inputValue' in option
            ? (option.inputValue as string)
            : option.text ?? ''
        }
        isOptionEqualToValue={(option, value) =>
          'id' in option && 'id' in value ? option.id === value.id : false
        }
        filterOptions={(options, params) => {
          const filtered = filter(options, params)
          // Toujours ajouter l'option "créer" à la fin
          filtered.push({ inputValue: params.inputValue, text: '__CREATE__' })
          return filtered
        }}
        // size="small"
        onChange={handleChange}
        disableCloseOnSelect
        open={open}
        onOpen={() => setOpen(true)}
        onClose={(_, reason) => {
          if (reason === 'blur' && isCreatingNew) return
          setOpen(false)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={hideLabel ? undefined : t('tags')}
            placeholder={placeholder}
            sx={inputSx}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((tag, index) => {
            if (!('id' in tag)) return null
            const { key, ...tagProps } = getTagProps({ index })
            return (
              <Chip
                key={key}
                {...tagProps}
                label={tag.text}
                size="small"
                sx={(theme) => getTagChipSx(tag.color, theme.palette.mode)}
              />
            )
          })
        }
        renderOption={(props, option) => {
          // Option "créer un tag"
          // if ('text' in option && option.text === '__CREATE__') {
          //   const { key, ...optionProps } = props as {
          //     key: string
          //   } & React.HTMLAttributes<HTMLLIElement>
          //   return (
          //     <li
          //       key={key}
          //       {...optionProps}
          //       style={{ padding: 0, listStyle: 'none' }}
          //     >
          //       {isCreatingNew ? (
          //         <TagCreateForm
          //           initialText={createInitialText}
          //           onSubmit={handleCreate}
          //           onCancel={() => {
          //             setIsCreatingNew(false)
          //             setCreateInitialText('')
          //           }}
          //           isLoading={isCreating}
          //         />
          //       ) : (
          //         <Stack
          //           direction="row"
          //           alignItems="center"
          //           gap={1}
          //           onMouseDown={(e) => {
          //             e.preventDefault()
          //             e.stopPropagation()
          //             setIsCreatingNew(true)
          //             setCreateInitialText(
          //               ('inputValue' in option
          //                 ? (option.inputValue as string)
          //                 : '') ?? ''
          //             )
          //           }}
          //           sx={{
          //             px: 1.5,
          //             py: 1,
          //             cursor: 'pointer',
          //             color: 'text.secondary',
          //             width: '100%',
          //             borderTop: '1px solid',
          //             borderColor: 'divider',
          //             '&:hover': {
          //               bgcolor: 'action.hover',
          //               color: 'primary.main',
          //             },
          //           }}
          //         >
          //           <AddIcon sx={{ fontSize: 16 }} />
          //           <Typography variant="body2" sx={{ fontSize: 13 }}>
          //             {t('createTag')}
          //           </Typography>
          //         </Stack>
          //       )}
          //     </li>
          //   )
          // }

          if (!('id' in option)) return null
          const tag = option
          const isSelected = selectedTagIds.includes(tag.id ?? '')
          // const isEditing = editingTag?.id === tag.id
          // const isDeleting = deletingId === tag.id

          // if (isEditing) {
          //   return (
          //     <li key={tag.id} style={{ padding: 0, listStyle: 'none' }}>
          //       <TagEditForm
          //         tag={tag}
          //         onSubmit={handleUpdate}
          //         onCancel={() => setEditingTag(null)}
          //         isLoading={isPatching}
          //       />
          //     </li>
          //   )
          // }

          const { key, ...optionProps } = props as {
            key: string
          } & React.HTMLAttributes<HTMLLIElement>
          return (
            <li key={key} {...optionProps}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  width: '100%',
                  '&:hover .tag-opt-actions': { opacity: 1 },
                }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Box
                    sx={(theme) => ({
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: getTagDotColor(tag.color, theme.palette.mode),
                      flexShrink: 0,
                    })}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: 12, fontWeight: isSelected ? 600 : 400 }}
                  >
                    {tag.text}
                  </Typography>
                  {/* {isGeneric(tag) && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.disabled', fontSize: 10 }}
                    >
                      global
                    </Typography>
                  )} */}
                </Stack>
                {/* {!isGeneric(tag) && (
                  <Stack
                    direction="row"
                    className="tag-opt-actions"
                    sx={{ opacity: 0, transition: 'opacity 0.15s' }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        setEditingTag(tag)
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleDelete(tag, e)}
                      disabled={isDeleting}
                      sx={{ p: 0.5 }}
                    >
                      {isDeleting ? (
                        <CircularProgress size={12} />
                      ) : (
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      )}
                    </IconButton>
                  </Stack>
                )} */}
              </Stack>
            </li>
          )
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 1.5,
              boxShadow: 3,
              mt: 0.5,
              '& .MuiAutocomplete-option': { minHeight: 30, py: 0.5, px: 1.25 },
              '& .MuiAutocomplete-listbox': { py: 0.5 },
            },
          },
          popper: {
            ref: popperRef,
          },
        }}
      />
    </Box>
  )
}
